import React, { PureComponent } from 'react'
import RangeCalendar from 'rc-calendar/lib/RangeCalendar'
import moment from 'moment'
import PropTypes from 'prop-types'
import debounceRender from 'react-debounce-render'
import { entries } from 'lodash'
import 'rc-calendar/assets/index.css'
import './Calendar.scss'

const DEBOUNCE_DURATION = 300 // ms

class Calendar extends PureComponent {
  static propTypes = {
    onDateRangeChange: PropTypes.func.isRequired,
    selectedDateRange: PropTypes.object.isRequired,
    berthsOccupation: PropTypes.object,
    quays: PropTypes.array.isRequired
  }

  constructor(props) {
    super(props)

    // we keep a copy of the selected date range in the state to have a smooth
    // debouncing: when updating from/to from within Calendar, it should not
    // temporarily switch to the old from/to until the debounced changes come in
    this.state = {
      from: props.selectedDateRange.from,
      to: props.selectedDateRange.to
    }
  }

  componentDidUpdate(prevProps) {
    const { from, to } = this.props.selectedDateRange

    if (from !== prevProps.selectedDateRange.from) {
      this.setState({ from })
    }
    if (to !== prevProps.selectedDateRange.to) {
      this.setState({ to })
    }
  }

  render() {
    const selectedDateRange = [moment(this.state.from), moment(this.state.to)]

    return (
      <div className="calendar-container">
        <RangeCalendar
          dateRender={this.renderCalendarDateCell}
          onSelect={this.onDateRangeChange}
          selectedValue={selectedDateRange}
          key={moment(this.state.from).month()} //https://github.com/react-component/calendar/issues/643
        />
      </div>
    )
  }

  /**
   * Render function for each date cell in the calendar widget
   */
  renderCalendarDateCell = currentDate => {
    const isPastDate = currentDate.valueOf() < moment().valueOf()
    const classNamePastDate = isPastDate ? ' past ' : ''

    // TODO: calculating busyness is quite heavy. We could calculate this once and keep it in redux or in cache
    const matchingTerminalOccupation = this.calculateBusyness(this.props.berthsOccupation, currentDate)

    return (
      <div className={'date-cell' + classNamePastDate + ' ' + matchingTerminalOccupation.dayColour}>
        <span className="value">{currentDate.format('DD')}</span>
        <ul className="available-berth-slots-wrapper">
          {this.renderBusynessOfTheBerths(matchingTerminalOccupation.quaysOccupation)}
        </ul>
      </div>
    )
  }

  /**
   * Render the busyness per berth
   */
  renderBusynessOfTheBerths = quaysOccupation => {
    if (quaysOccupation) {
      return this.props.quays.map(q => {
        const relatedQuayOccupation = quaysOccupation.find(qO => qO.quay === q.name)

        if (relatedQuayOccupation) {
          return (
            <li key={relatedQuayOccupation.quay}>
              <ul>
                {relatedQuayOccupation.berthOccupation.map((berthOccupation, i) => (
                  <li
                    className={`available-berth-slot ${this.getColourBasedOnPercentage(berthOccupation.occupation)}`}
                    key={i}
                  />
                ))}
              </ul>
            </li>
          )
        }

        return null
      })
    }

    return null
  }

  /**
   * When user selects date range
   * @param rangeArray
   */
  onDateRangeChange = rangeArray => {
    const from = rangeArray[0].valueOf()
    const to = rangeArray[1].valueOf()

    this.props.onDateRangeChange({ from, to })
  }

  calculateBusyness = (terminalOccupation, currentDate) => {
    const matchingTerminalOccupation =
      terminalOccupation &&
      entries(terminalOccupation)
        .map(tO => {
          const occupancy = tO[1].find(
            bO => moment(bO.date).startOf('day').valueOf() === moment(currentDate).startOf('day').valueOf()
          )

          if (occupancy) {
            return {
              ...occupancy,
              quay: tO[0]
            }
          }

          return null
        })
        .filter(tO => tO)

    if (matchingTerminalOccupation && matchingTerminalOccupation.length) {
      const average =
        matchingTerminalOccupation
          .map(tO => tO.berthOccupation.map(bO => bO.occupation))
          .reduce((acc, val) => acc.concat(val), [])
          .reduce((a, b) => a + b, 0) / matchingTerminalOccupation.length
      const dayColour = this.getColourBasedOnPercentage(average)

      return {
        dayColour,
        quaysOccupation: matchingTerminalOccupation
      }
    }

    return {
      dayColour: 'white',
      quaysOccupation: null
    }
  }

  getColourBasedOnPercentage = percentage => {
    let colour = 'red'
    if (percentage < 33) {
      colour = 'green'
    } else if (percentage < 66) {
      colour = 'orange'
    }
    return colour
  }
}

export default debounceRender(Calendar, DEBOUNCE_DURATION)
